import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  isAxiosError,
} from "axios";
import { clearAuthStorage, getCookie } from "composables/useHelpers";
import { ref } from "vue";

// Constants
const XSRF_COOKIE_NAME = "XSRF-TOKEN";
const XSRF_HEADER_NAME = "X-XSRF-TOKEN";

// Reactive impersonation state updated from X-Impersonating response header
const isImpersonating = ref<boolean>(false);

const apiService: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_LARAVEL_URL,
  withCredentials: true,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    Accept: "application/json",
  },
});

/**
 * Builds the XSRF token cookie name based on the environment.
 * Production uses "XSRF-TOKEN", other environments prefix with the env name.
 */
const getXsrfCookieName = (): string => {
  const env = import.meta.env.VITE_BACKEND_APP_ENV;

  return env === "production"
    ? XSRF_COOKIE_NAME
    : `${env.toUpperCase()}-${XSRF_COOKIE_NAME}`;
};

apiService.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getCookie(getXsrfCookieName());

  if (token) {
    config.headers.set(XSRF_HEADER_NAME, token);
  }

  return config;
});

// Response / error handling
apiService.interceptors.response.use(
  (response: AxiosResponse) => {
    // Update impersonation state from response header
    const impersonatingHeader = response.headers["x-impersonating"];
    if (impersonatingHeader !== undefined) {
      isImpersonating.value = impersonatingHeader === "true";
    }

    return response;
  },
  async (error: AxiosError) => {
    const { status = null } = error?.response || {};
    const original = error.config as AxiosRequestConfig;

    // Avoid handling refresh endpoint itself
    const url: string = original?.url || "";

    if (status === 401 && !url.includes("/oauth/login")) {
      // Reset impersonation state on logout
      isImpersonating.value = false;

      clearAuthStorage();

      // Navigate to login instead of reload to avoid interrupting active module imports
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);
const orvalAxiosInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  return apiService(config).then((response) => response.data);
};

export { apiService, isAxiosError, isImpersonating, orvalAxiosInstance };
